<script>
  /*____________ Config  ____________*/
  export let tour;
  export let fromEmbed = false;
  export let embedId = null;
  
  /*____________ Imports ____________*/
  import { onDestroy, onMount } from "svelte";
  import { fly } from "svelte/transition";
  import i18n from "./i18n/i18n";

  const FLY_DURATION = 500;
  const FLY_FROM_BOTTOM = { y: 300, duration: FLY_DURATION };
  const NO_FLY = { y: 0, duration: 0 };
  import { tourFunctions } from "../tour-functions";
  const { convertColor } = tourFunctions;
  import {
    getMapboxViewFromInstance,
    isMapEntryHash,
    parseMapUrlHash,
  } from "./mapUrlHash.js";
  import { trackSceneViewStart, initAnalytics } from "./analytics";
  
  // Initialize analytics (Mixpanel, PostHog, and Statsig will be initialized if analyticsExperimental feature flag is present)
  (async () => {
    await initAnalytics(tour);
  })();

  /* ____________ Mobile Detection ____________ */
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";
  const isMobileOrTablet = (ua.getResult().device["type"] === "mobile" || ua.getResult().device["type"] === "tablet");

  /*____________ Components ____________*/
  import Krpano from "./Krpano.svelte";
  import Mapbox from "./Mapbox.svelte";
  import ImageMap from "./ImageMap.svelte";
  import SwitchViews from "./SwitchViews.svelte";
  import MapButton from "./MapButton.svelte";
  import InfoSidebar from "./InfoSidebar.svelte";
  import Logo from "./Logo.svelte";
  import HamburgerMenu from "./HamburgerMenu.svelte";
  import NextScene from "./NextScene.svelte";
  import PrevScene from "./PrevScene.svelte";
  import WatermarkLogo from "./WatermarkLogo.svelte";
  import ThumbsGrid from "./ThumbsGrid.svelte";
  import SceneTitle from "./SceneTitle.svelte";
  import Video from "./Video.svelte";
  import SceneDescription from "./SceneDescription.svelte";
  import PopupHotspot from "./PopupHotspot.svelte";
  import PopupAccessibilityHotspot from "./PopupAccessibilityHotspot.svelte";
  import PopupSocial from "./PopupSocial.svelte";
  import SceneButton from "./SceneButton.svelte";
  import PopupGallery from "./PopupGallery.svelte";
  import CallToAction from "./CallToAction.svelte";
  import FormPopup from "./FormPopup.svelte";
  import Popup from "./Popup.svelte";
  import IntroPopup from "./IntroPopup.svelte";
  import Ada from "./Ada.svelte";
  import Instructions from "./Instructions.svelte";
  import TutorialOverlay from "./TutorialOverlay.svelte";
  import TextLogo from "./TextLogo.svelte";
  import PersonalizationButton from "./PersonalizationButton.svelte";
  import MobileControls from "./MobileControls.svelte";
  import Loader from "./Loader.svelte";
  import { createTutorialOverlay } from "./tutorialOverlay.js";
  import { trackHotspotClick } from "./analytics";
  import mapboxglSupported from '@mapbox/mapbox-gl-supported';
  import { iconifyDirectionIconUrl } from "./utils";
  
  const { callToAction, audioSettings, menu, theme, form } = tour;
  const embedAdjust = fromEmbed ? 60 : 0;   // px adjustment for close button on embed
  const sceneButton = tour.sceneButton;
  const colors = {
    themeColor: convertColor(theme?.primaryColor || "#000000"),
    controlsTextColor: convertColor(theme?.controlsTextColor || "#FFFFFF"),
    loaderColor: convertColor(theme?.loaderColor || "#FFFFFF"),
    loaderBg: convertColor(theme?.loaderBgColor || "#000000"),
    loaderTextColor: convertColor(theme?.loaderTextColor || "#FFFFFF"),
    hotspotHoverColor: convertColor(theme?.hotspotColor || "#000000"),
    calloutColor: convertColor(
      theme?.calloutColor || theme?.primaryColor || "#000000",
    ),
    hotspotColor: convertColor(theme?.hotspotColor || "#000000"),
    hotspotTextColor: convertColor(theme?.hotspotTextColor || "#FFFFFF"),
    popupTextColor: convertColor(theme?.popupTextColor || "#FFFFFF"),
    popupBgColor: convertColor(
      theme?.popupBgColor || theme?.primaryColor || "#FFFFFF",
    ),
    sceneButtonBgColor: convertColor(
      sceneButton?.bgColor || theme?.primaryColor || "#000000",
    ),
    sceneButtonTextColor: convertColor(sceneButton?.textColor || "#FFFFFF"),
    formPopupTextColor: convertColor(
      form?.popupTextColor || theme?.popupTextColor || "#FFFFFF",
    ),
    formPopupBgColor: convertColor(
      form?.popupBgColor ||
        theme?.popupBgColor ||
        theme?.primaryColor ||
        "#000000",
    ),
    focusColor: convertColor(theme?.focusColor || "#f7c60f"),
  };
  tourFunctions.setupColors(colors);
  const fonts = {
    fontHeading: tour.fontHeading || "Oswald",
    fontBody: tour.fontBody || "Raleway",
  };
  tourFunctions.setupFonts(fonts);

  /*____________ Variables ____________*/
  let liveFeedback;
  const listeners = [];
  let loaderDone = false;
  let visibleUi = false;
  /** ms delay for showUi after loaderDone; set by littleplanetend when UI should reveal */
  let pendingUiRevealDelay = null;
  let mapVisible = false;
  let video;
  let mobileDescriptionOpen = false;
  let showIntroPopup = true;
  const hotspotHashForLittlePlanet = window.location.hash.match(/^#\/hotspot\/(.+)$/);
  const hotspotIdForLittlePlanet = hotspotHashForLittlePlanet ? hotspotHashForLittlePlanet[1] : null;

  /** Mirrors Krpano: whether the little-planet intro actually runs (tour + URL + layout). */
  function hasLittlePlanetIntroEffective(expandedTourMode) {
    let lp = tour?.hasLittlePlanet ?? true;
    if (typeof window === "undefined") return false;
    if (
      hotspotIdForLittlePlanet ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      lp = false;
    }
    const adaVal = new URLSearchParams(window.location.search).get("ada");
    if (adaVal === "true" || adaVal === "") lp = false;
    if (tour?.features?.includes("removeIntroAnimation")) lp = false;
    if (!expandedTourMode) lp = false;
    return lp;
  }

  $: hasIntroContent = !!(
    tour.introPopup?.heading ||
    tour.introPopup?.logoUrl ||
    tour.introPopup?.contentHtml ||
    tour.introPopup?.videoEmbedUrl ||
    (tour.introPopup?.showRoutes &&
      tour.features?.includes("routes") &&
      (tour.routes?.filter((r) => r.featured)?.length ?? 0) > 0)
  );
  // Scene state management
  let sceneState = {
    allScenes: tour.scenes?.filter(scene => !scene.hidden && !scene.burried) || [],
    currentOrder: tour.scenes?.filter(scene => !scene.hidden && !scene.burried) || [],
    isPersonalizedTour: false
  };

  // Function to update scene order (called by ThumbsGrid)
  function updateSceneOrder(newOrder, isPersonalized = false) {
    sceneState.currentOrder = newOrder;
    sceneState.isPersonalizedTour = isPersonalized;
    if (window.CC) {
      window.CC.currentSceneOrder = newOrder;
    }
  }

  // Check if the user has webgl support
  const hasStaticSceneTitles = Boolean(tour.features?.includes("staticSceneTitles"));
  
  // ensure map is existing, enabled, & supported
  const hasMap =
    typeof tour.mapbox === "object" &&
    tour.features?.includes("map") &&
    tour.mapbox?.enabled &&
    (tour.mapbox?.mapDisplayMode === "image" || mapboxglSupported.supported());

  if (tour.features?.includes("map") && !hasMap) {
    console.error("Map feature is enabled but no mapbox settings provided.");
  }

  // Track tour view if tracking feature is enabled
  const hasTracking = Boolean(tour.features?.includes("tracking"));
  if (hasTracking && tour.id) {
    trackTourView().catch(error => {
      console.error('Error tracking tour view:', error);
    });
  }

  let hasCurrentSceneAudio = false;
  const hasAnySceneAudio = Boolean(tour.scenes.some((scene) => scene.audioId || scene.descriptionAudio?.assetId));
  const isMapboxMap = hasMap && tour.mapbox?.mapDisplayMode !== "image";
  const mapHash = isMapboxMap
    ? parseMapUrlHash(window.location.hash)
    : { isMap: isMapEntryHash(window.location.hash), view: null };
  const initialMapView = mapHash.view;
  const showMapFirst = mapHash.isMap;
  let expandedMap = showMapFirst;
  let expandedTour = !showMapFirst;
  let mapLoading = showMapFirst; // Only load when starting with map
  let skipNextMapUrlSync = !!initialMapView;
  /** Set true on first `krpano:littleplanetend`; intro waits on this when little planet runs. */
  let littlePlanetIntroDone = false;

  $: introWaitsOnLittlePlanet =
    showIntroPopup &&
    tour.features?.includes("introPopup") &&
    tour.introPopup?.enabled &&
    hasIntroContent &&
    hasLittlePlanetIntroEffective(expandedTour);

  $: showIntroPopupVisible =
    (loaderDone || visibleUi) &&
    showIntroPopup &&
    tour.features?.includes("introPopup") &&
    tour.introPopup?.enabled &&
    hasIntroContent &&
    (!introWaitsOnLittlePlanet || littlePlanetIntroDone);

  /* Tutorial overlay: scheduling, deferral blockers (form/intro), and logging live in tutorialOverlay.js */
  /** null = not yet shown, true = visible, false = dismissed (once per session) */
  let showTutorialOverlay = null;
  const tutorialOverlay = createTutorialOverlay({
    onShowChange: (value) => {
      showTutorialOverlay = value;
    },
    getContext: () => ({
      showTutorialOverlay,
      expandedTour,
      visibleUi,
      loaderDone,
      hasLittlePlanetIntro: hasLittlePlanetIntroEffective(expandedTour),
      formEnabled: !!(form?.enabled && !hasAdaParam),
      formDelay: form?.delay ?? null,
      showIntroPopupVisible,
      adaVisible,
      instructionsVisible,
      popupSocialOpen: !!(popupSocial?.url && popupSocial?.platform),
    }),
  });
  $: showIntroPopupVisible, visibleUi, hasAdaParam, tutorialOverlay.syncBlockers();

  $: if (loaderDone && pendingUiRevealDelay !== null) {
    const delay = pendingUiRevealDelay;
    pendingUiRevealDelay = null;
    if (delay > 0) {
      setTimeout(() => showUi(), delay);
    } else {
      showUi();
    }
  }

  /* No little planet: start-scene hotspots stay hidden until tutorial overlay (tour.xml intro_hs_pending -> reveal_intro_hs). */
  $: if (
    showTutorialOverlay === true &&
    expandedTour &&
    !hasLittlePlanetIntroEffective(expandedTour)
  ) {
    tourFunctions.showIntroHotspotsFromTutorial();
  }

  function dismissTutorialOverlay(reason = "unknown") {
    tutorialOverlay.dismiss(reason);
    if (window.CC?.krpano) window.CC.krpano.call("cancel_intro();");
    queuePanoFocus();
  }

  const audioGestureUnlockHandler = () => {
    if (tutorialOverlay.isShowing() && expandedTour) {
      dismissTutorialOverlay("audio-gesture-unlock");
    }
  };

  onMount(() => tutorialOverlay.mount());

  let mobileLocationsOpen = false;
  let focusLocationsFilterOnOpen = false;
  const showLocationMenu = tour?.mapbox?.showLocationMenu !== false;
  $: if (!showLocationMenu && (mobileLocationsOpen || focusLocationsFilterOnOpen)) {
    mobileLocationsOpen = false;
    focusLocationsFilterOnOpen = false;
  }

  const hasSceneLogos = tour.features?.includes("sceneLogos");
  const logoText = tour.logoText;
  
  // Tour chrome logo: disabled applies per viewport only; no cross-viewport fallback when the active slot is disabled.
  // - Desktop: tour.logo.disabled blocks primary; no fallback to logoMobile. Fallback to logoMobile only when primary is not disabled but has no url.
  // - Mobile: tour.logoMobile.disabled blocks mobile slot; no fallback to tour.logo. Fallback to tour.logo when mobile slot not disabled but empty (logo.disabled ignored on mobile).
  function getCurrentLogo() {
    if (hasSceneLogos && currentSceneName) {
      const currentScene = tour.scenes.find((scene) => scene.name === currentSceneName);
      if (currentScene?.logo?.url && !currentScene.logo?.disabled) {
        const sceneLogo = currentScene.logo;
        // Use mobile dimensions if on mobile and they exist, otherwise use desktop dimensions
        if (isMobile && (sceneLogo.mobileWidth || sceneLogo.mobileHeight)) {
          return {
            ...sceneLogo,
            width: sceneLogo.mobileWidth ?? sceneLogo.width,
            height: sceneLogo.mobileHeight ?? sceneLogo.height,
          };
        }
        return sceneLogo;
      }
    }
    if (isMobile) {
      if (tour.logoMobile?.disabled) return null;
      if (tour.logoMobile?.url) {
        const lm = tour.logoMobile;
        if (lm.mobileWidth || lm.mobileHeight) {
          return {
            ...lm,
            width: lm.mobileWidth ?? lm.width,
            height: lm.mobileHeight ?? lm.height,
          };
        }
        return lm;
      }
      return tour.logo?.url ? tour.logo : null;
    }
    if (tour.logo?.disabled) return null;
    if (tour.logo?.url) return tour.logo;
    if (tour.logoMobile?.url) return tour.logoMobile;
    return null;
  }

  let currentSceneName = null;
  const emptyTourLogo = () => ({
    url: null,
    width: 150,
    height: 60,
    text: logoText || "Logo",
  });
  let tourLogo = getCurrentLogo() ?? emptyTourLogo();
  
  const updateHasSceneAudio = (e) => {
    currentSceneName = e?.detail?.scene;
    if (!currentSceneName) return;
    const currentScene = tour.scenes.find((scene) => scene.name === currentSceneName);
    if (currentScene?.audioId) {
      hasCurrentSceneAudio = true;
    } else {
      hasCurrentSceneAudio = false;
    }
    // Update logo when scene changes if sceneLogos feature is enabled
    if (hasSceneLogos) {
      tourLogo = getCurrentLogo() ?? emptyTourLogo();
    }
  }
  addEventListener("krpano:newpano", updateHasSceneAudio);
  listeners.push({ type: "krpano:newpano", listener: updateHasSceneAudio });
  
  // Reactive statement to update logo when scene changes
  $: {
    if (hasSceneLogos && currentSceneName) {
      tourLogo = getCurrentLogo() ?? emptyTourLogo();
    }
  }

  // Map-expanded header: mapLogo.disabled is desktop-only; mapLogoMobile.disabled is mobile-only. No tourLogo fallback when that viewport's map slot is disabled.
  $: mapLogo = (() => {
    if (isMobile) {
      if (tour.mapLogoMobile?.disabled) return null;
      if (tour.mapLogoMobile?.url) return tour.mapLogoMobile;
      if (tour.mapLogo?.url) return tour.mapLogo;
      return tourLogo;
    }
    if (tour.mapLogo?.disabled) return null;
    if (tour.mapLogo?.url) return tour.mapLogo;
    if (tour.mapLogoMobile?.url) return tour.mapLogoMobile;
    return tourLogo;
  })();
  // Loading overlay: hide image when that slot is disabled (still show loader chrome)
  $: logoLoadingSource =
    (isMobile && tour.logoLoadingMobile?.url
      ? tour.logoLoadingMobile
      : tour.logoLoading) || tourLogo;
  $: logoLoading = logoLoadingSource?.disabled
    ? { ...logoLoadingSource, url: null }
    : logoLoadingSource;
  const tourTitle = tourFunctions.parseContent(tour.name);
  const sceneButtonSettings = tour.sceneButton ? tour.sceneButton : {};
  // audio
  let sceneAudioPlaying;
  // Map variables
  let zoom = initialMapView?.zoom ?? 14;
  const setMapZoom = () => {
    if (isMobile && tour?.mapbox?.mobile) {
      // For mobile devices with mobile mapbox settings
      const mobileSettings = tour.mapbox.mobile;
      const defaultSettings = tour.mapbox;
      
      zoom = expandedMap
        ? mobileSettings?.zoomExpandedMap ?? defaultSettings.zoomExpandedMap
        : mobileSettings?.zoomMiniMap ?? defaultSettings.zoomMiniMap;
    } else {
      // For desktop or no mobile settings
      const { zoomExpandedMap, zoomMiniMap } = tour.mapbox;
      zoom = expandedMap ? zoomExpandedMap : zoomMiniMap;
    }
    return zoom;
  };

  const visibleScenes = tour.scenes.filter((scene) => !scene.hidden);

  const getDimension = (autoSize, defaultValue, desktopValue, mobileValue) => {
    // is autoSize enabled?
    if (autoSize) return defaultValue;
    // was a desktop value provided?
    if (!isMobile && desktopValue) return desktopValue;
    // was a mobile value provided?
    if (isMobile && mobileValue) return mobileValue;
    // no values provided, return default
    return defaultValue;
  };

  let sceneButtonLabel = sceneButtonSettings?.label ?? $i18n.t('tour.sceneButton');
  
  // scene button width
  const defaultWidth = tourLogo.width * 0.8;
  const sceneButtonWidth = getDimension(
    sceneButtonSettings?.autoSize !== false,
    defaultWidth,
    sceneButtonSettings.width,
    sceneButtonSettings.mobileWidth,
  );

  // scene button x position
  const defaultX = tourLogo.width / 2 - sceneButtonWidth / 2 + 20;
  let sceneButtonX = getDimension(
    sceneButtonSettings?.autoSize !== false,
    defaultX,
    sceneButtonSettings.x,
    sceneButtonSettings.mobileX,
  );
  sceneButtonX =
    !tourLogo.url &&
    logoText &&
    (isMobile ? !tour.logoMobile?.disabled : !tour.logo?.disabled)
      ? 20
      : sceneButtonX;

  // scene button y position
  const defaultY = tourLogo.height + 25;
  const sceneButtonY = getDimension(
    sceneButtonSettings?.autoSize !== false,
    defaultY,
    sceneButtonSettings.y,
    sceneButtonSettings.mobileY,
  );

  document.addEventListener("focusin", (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("focusin", e.target);
    }
  });

  /** Look-around / zoom via krpano forces (works without viewer focus; synthetic key events do not). */
  const LOOK_FORCE_KEYS = new Set([
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "-",
    "=",
  ]);

  function isTourChromeBlockingKeys() {
    const popup = document.querySelector(".popup");
    const thumbs = document.querySelector(".thumbs-container");
    const isThumbsOpen = thumbs && thumbs.classList.contains("active");
    return !!(popup || isThumbsOpen);
  }

  function applyKrpanoLookForce(keyName, pressed) {
    const k = window.CC?.krpano;
    if (!k) return false;
    const force = pressed ? 1 : 0;
    switch (keyName) {
      case "ArrowLeft":
        k.set("hlookat_moveforce", pressed ? -1 : 0);
        return true;
      case "ArrowRight":
        k.set("hlookat_moveforce", force);
        return true;
      case "ArrowUp":
        k.set("vlookat_moveforce", pressed ? -1 : 0);
        return true;
      case "ArrowDown":
        k.set("vlookat_moveforce", force);
        return true;
      case "-":
        // Zoom out
        k.set("fov_moveforce", force);
        return true;
      case "=":
        // Zoom in
        k.set("fov_moveforce", pressed ? -1 : 0);
        return true;
      default:
        return false;
    }
  }

  function handleLookKeyDown(keyName) {
    if (isTourChromeBlockingKeys()) return;
    tourFunctions.focus();
    applyKrpanoLookForce(keyName, true);
  }

  function handleLookKeyUp(event) {
    if (!LOOK_FORCE_KEYS.has(event.key)) return;
    if (!expandedTour) return;
    applyKrpanoLookForce(event.key, false);
  }

  // Stop AutoNav on user interaction
  document.addEventListener("mousedown", tourFunctions.stopAutoNav);
  listeners.push({ type: "mousedown", listener: tourFunctions.stopAutoNav, target: document });
  
  document.addEventListener("touchstart", tourFunctions.stopAutoNav);
  listeners.push({ type: "touchstart", listener: tourFunctions.stopAutoNav, target: document });

  /**
   * Map/tour switch controls must win over krpano's Enter hotkey.
   * With pano focus enabled, krpano can see Enter before the button's click
   * activation — handle these in capture and stop propagation.
   */
  const MAP_SWITCH_SELECTOR = ".view-switch-button, .map-button, .tour-button";
  const mapSwitchKeydownCapture = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(MAP_SWITCH_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    switchView();
  };
  document.addEventListener("keydown", mapSwitchKeydownCapture, true);
  listeners.push({
    type: "keydown",
    listener: mapSwitchKeydownCapture,
    target: document,
    capture: true,
  });

  const keydownListener = function (event) {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("Keydown event", event);
    }
    // Ignore synthetic events to prevent infinite loops
    if (event.isSynthetic) return;

    // if the tour is not expanded, don't allow any keydown events
    if (!expandedTour) return;

    // Check for popup and ignore hotkeys if it's open
    const hasPopup = document.querySelector(".popup");
    const hasThumbs = document.querySelector(".thumbs-container.active");
    if (hasPopup || hasThumbs) return;

    switch (event.key) {
      // Hide/Show UI
      case "H":
          visibleUi = !visibleUi;
          if (visibleUi) {
            tourFunctions.showHotspots();
            liveFeedback.textContent = $i18n.t('tour.hotkeyInterfaceHide');
          } else {
            tourFunctions.hideHotspots();
            liveFeedback.textContent = $i18n.t('tour.hotkeyInterfaceShow');
          }
        break;
      // Start / Stop AutoNav
      case "A":
          const autoNavStatus = tourFunctions.getAutoNavStatus();
          if (!autoNavStatus) {
            // autoNav will be enabled
            const autoNavInterval = prompt($i18n.t('tour.autoNavInterval'), "10");
            tourFunctions.stopAutoNav();
            tourFunctions.loadSceneByIndex(0, 'auto_nav');
            setTimeout(() => {
              tourFunctions.startAutoNav(autoNavInterval);
            }, 1000);
          } else {
            // autoNav will be disabled
            tourFunctions.stopAutoNav();
          }
          liveFeedback.textContent = $i18n.t('tour.hotkeyAutoNav');
        break;
      // Take a Screenshot
      case "S":
        // prompt for width and height
        const width = prompt($i18n.t('tour.screenshotWidth'), window.innerWidth);
        const height = prompt($i18n.t('tour.screenshotHeight'), window.innerHeight);
        const screenshot = tourFunctions.currentScene.screenshot(width, height, false, 'png', 1.0);
        // save the screenshot
        if (screenshot) {
          const filenamize = (str) => str.replace(/\s/g, "-").toLowerCase();
          const sceneTitle = tourFunctions.currentScene.getTitle();
          const filename = filenamize(tour.name + "-" + sceneTitle) + ".png";
          const downloadLink = document.createElement("a");
          downloadLink.href = screenshot;
          downloadLink.download = filename;
          downloadLink.click();
        }

        break;
      // Show Instructions
      case "i":
        toggleInstructions();
        if (instructionsVisible) {
          liveFeedback.textContent = $i18n.t('tour.hotkeyShowInstructions');
        } else {
          liveFeedback.textContent = $i18n.t('tour.hotkeyHideInstructions');
        }
        break;
      // Refocus the krpano element
      case "Escape":
        // check if a popup is open
        const popup = document.querySelector(".popup");
        if (popup) return;
        
        // If we're in an embed context (iframe), handle embed close
        if (fromEmbed || window.parent !== window) {
          if (embedId) {
            window.parent.postMessage({
              type: 'covecreek:close',
              embedId: embedId
            }, '*');
          }
        } else {
          // Only handle escape for non-embed contexts
          tourFunctions.focus();
          liveFeedback.textContent = $i18n.t('tour.hotkeyEscape');
        }
        break;
      // Navigate the scene (direct krpano forces — no synthetic key events)
      case "ArrowLeft":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowDown":
      case "-":
      case "=":
        event.preventDefault();
        // Ignore key repeat; force stays applied until keyup
        if (!event.repeat) handleLookKeyDown(event.key);
        break;
      // Next/Previous Scene
      case ",":
        event.preventDefault();
        tourFunctions.prevSceneWithOrder(sceneState.currentOrder, 'keyboard');
        break;
      case ".":
        event.preventDefault();
        tourFunctions.nextSceneWithOrder(sceneState.currentOrder, 'keyboard');
        break;
    }
  };
  document.addEventListener("keydown", keydownListener);
  listeners.push({ type: "keydown", listener: keydownListener, target: document });
  document.addEventListener("keyup", handleLookKeyUp);
  listeners.push({ type: "keyup", listener: handleLookKeyUp, target: document });

  /*____________ Functions ____________*/
  /** Focus the pano once UI is interactive so wheel/keys work without a prior click. */
  function queuePanoFocus() {
    if (!expandedTour) return;
    const tryFocus = (attemptsLeft) => {
      requestAnimationFrame(() => {
        if (
          showIntroPopupVisible ||
          adaVisible ||
          instructionsVisible ||
          (popupSocial?.url && popupSocial?.platform)
        ) {
          return;
        }
        if (tourFunctions.focus()) return;
        if (attemptsLeft > 0) tryFocus(attemptsLeft - 1);
      });
    };
    tryFocus(3);
  }

  function showUi() {
    visibleUi = true;
    // Show map when UI becomes visible
    mapVisible = true;
    tutorialOverlay.onUiVisible();
    queuePanoFocus();
  }

  /** Fullscreen gallery zoom+minimap: tour JSON (per hotspot) wins; else krpano `gallery_zoom_minimap` from published XML. */
  function isGalleryZoomMinimapEnabled(krpanoHotspot) {
    if (!krpanoHotspot) return false;
    const id = krpanoHotspot.id;
    if (id && tour?.scenes?.length) {
      try {
        const sceneId = tourFunctions.currentScene.getId();
        const scene = tour.scenes.find((s) => s.id === sceneId);
        const h = scene?.hotspots?.find((x) => x.id === id);
        if (h) {
          if (h.galleryZoomMinimap === true) return true;
          if (h.galleryZoomMinimap === false) return false;
        }
      } catch (_e) {
        /* current scene not available yet */
      }
    }
    const v =
      krpanoHotspot.gallery_zoom_minimap ?? krpanoHotspot.galleryZoomMinimap;
    return v === true || v === "true" || v === 1 || v === "1";
  }

  // gets hotspot popup content; directions come from tour config JSON (scene.hotspots[].popupDirections)
  function getHotspotFromTourJson(hotspotId, sceneId) {
    const resolvedSceneId =
      sceneId ??
      (() => {
        try {
          return tourFunctions.currentScene.getId();
        } catch (_e) {
          return null;
        }
      })();
    if (!resolvedSceneId || !tour?.scenes?.length) return null;
    const scene = tour.scenes.find((s) => s.id === resolvedSceneId);
    return scene?.hotspots?.find((h) => h.id === hotspotId) ?? null;
  }

  function getHotspotContent(hotspot) {
    if (typeof hotspot === "string") {
      hotspot = tourFunctions.currentScene.getHotspot(hotspot);
      if (!hotspot) return;
    }

    let gallery = null;
    if (hotspot.popup_gallery) {
      gallery = tourFunctions.currentScene.getGallery(hotspot.popup_gallery);
    }

    const {
      id,
      name,
      popup_title,
      popup_text,
      popup_image,
      popup_image_alt,
      popup_video_embed,
      popup_directions,
    } = hotspot;
    const title = popup_title?.replace(/\[.*?\]/g, " ");
    const content = popup_text ? tourFunctions.parseContent(popup_text) : "";

    function directionsFromTourJson() {
      if (!id || !tour?.scenes?.length) return [];
      let sceneId;
      try {
        sceneId = tourFunctions.currentScene.getId();
      } catch (_e) {
        return [];
      }
      const scene = tour.scenes.find((s) => s.id === sceneId);
      const hs = scene?.hotspots?.find((h) => h.id === id);
      const raw = hs?.popupDirections;
      if (!Array.isArray(raw) || raw.length === 0) return [];
      return raw.map((d) => {
        const base = {
          lat: d.lat,
          long: d.long,
          type: d.type,
          label: d.label,
        };
        if (d.type === "custom") {
          return {
            ...base,
            icon: d.icon,
            iconUrl: d.icon ? iconifyDirectionIconUrl(d.icon) : null,
          };
        }
        return base;
      });
    }

    /** Legacy: old builds exposed pipe-separated lat,long,type on the krpano hotspot. */
    function parseLegacyPopupDirections(str) {
      if (!str) return [];
      const directionEntries = str.split("|").map((entry) => entry.trim()).filter(Boolean);
      return directionEntries
        .map((entry) => {
          const parts = entry.split(",").map((part) => part.trim());
          if (parts.length !== 3) return null;
          const [lat, long, type] = parts;
          return {
            lat: parseFloat(lat),
            long: parseFloat(long),
            type,
          };
        })
        .filter(Boolean);
    }

    let directions = directionsFromTourJson();
    if (!directions.length && popup_directions) {
      directions = parseLegacyPopupDirections(popup_directions);
    }

    const hotspotFromJson = getHotspotFromTourJson(id);
    const socialVideo = hotspotFromJson?.popupSocialUrl
      ? {
          url: hotspotFromJson.popupSocialUrl,
          content: hotspotFromJson.popupSocialContent,
          altText: hotspotFromJson.popupSocialAltText ?? "",
          hasDescription: hotspotFromJson.popupSocialHasDescription ?? false,
          platform: "instagram",
        }
      : null;

    return {
      id,
      name,
      title,
      image: popup_image,
      imageAlt: popup_image_alt,
      content,
      videoUrl: popup_video_embed,
      socialVideo,
      gallery,
      directions,
      enableGalleryZoomMinimap: isGalleryZoomMinimapEnabled(hotspot),
      handleVideoLoad: function () {
        video = popup_video_embed;
        sceneAudioPlaying = false;
      },
      handleSocialVideoLoad: function () {
        if (hotspotFromJson) {
          openSocialVideoFromHotspot(hotspotFromJson, "video");
        }
      },
    };
  }


  function openHotspotInfo(hotspot) {
    // if hotspotName was provided instead of {object}
    if (typeof hotspot === "string") {
      let hotspotName = hotspot;
      hotspot = getHotspotContent(hotspotName);
    }
    if (!hotspot) return;

    // Set the hotspot URL hash
    if (hotspot.id) {
      tourFunctions.setHotspotUrl(hotspot.id);
    }

    new PopupHotspot({
      target: document.getElementById("popup-container"),
      props: { ...hotspot },
    });
  }

  function openAccessibilityInfo(hotspot) {
    // if hotspotName was provided instead of {object}
    if (typeof hotspot === "string") {
      let hotspotName = hotspot;
      hotspot = getHotspotContent(hotspotName);
    }
    if (!hotspot) return;

    // Set the hotspot URL hash
    if (hotspot.id) {
      tourFunctions.setHotspotUrl(hotspot.id);
    }

    new PopupAccessibilityHotspot({
      target: document.getElementById("popup-container"),
      props: { ...hotspot },
    });
  }

  let gallery;
  let popupSocial = null;

  function openSocialVideoFromHotspot(hotspot, trackType = "social") {
    if (!hotspot?.popupSocialUrl) return;
    sceneAudioPlaying = false;
    popupSocial = {
      url: hotspot.popupSocialUrl,
      platform: "instagram",
      title: hotspot.title,
      content: hotspot.popupSocialContent,
      hasDescription: hotspot.popupSocialHasDescription ?? false,
      altText: hotspot.popupSocialAltText ?? "",
    };
    trackHotspotEvent(hotspot, trackType);
  }

  function openGalleryInfo(hotspot) {
    if (typeof hotspot === "string") {
      hotspot = tourFunctions.currentScene.getHotspot(hotspot);
    }
    if (!hotspot) return;
    console.log('openGalleryInfo', hotspot);

    // Set the hotspot URL hash
    if (hotspot.id) {
      tourFunctions.setHotspotUrl(hotspot.id);
    }

    gallery = new PopupGallery({
      target: document.getElementById("popup-container"),
      props: {
        name: hotspot.name,
        title: tourFunctions.parseContent(hotspot.popup_title),
        gallery: tourFunctions.currentScene.getGallery(hotspot.popup_gallery),
        enableZoomMinimap: isGalleryZoomMinimapEnabled(hotspot),
      },
    });
  }

  /*____________ Krpano Events ____________*/
  const krpanoLoaderdoneHandler = () => {
    loaderDone = true;
  };
  addEventListener("krpano:loaderdone", krpanoLoaderdoneHandler);
  listeners.push({ type: "krpano:loaderdone", listener: krpanoLoaderdoneHandler });

  const krpanoLittlePanetEndHandler = (e) => {
    littlePlanetIntroDone = true;
    if (e.detail?.showUi !== false && !introWaitsOnLittlePlanet) {
      const hasLittlePlanetIntro = hasLittlePlanetIntroEffective(expandedTour);
      pendingUiRevealDelay = hasLittlePlanetIntro ? 0 : 1500;
    }
  };
  addEventListener("krpano:littleplanetend", krpanoLittlePanetEndHandler);
  listeners.push({ type: "krpano:littleplanetend", listener: krpanoLittlePanetEndHandler });

  const krpanoNewPanoNavUnlockHandler = () => {
    tourFunctions.releaseSceneNavLock();
  };
  addEventListener("krpano:newpano", krpanoNewPanoNavUnlockHandler);
  listeners.push({
    type: "krpano:newpano",
    listener: krpanoNewPanoNavUnlockHandler,
  });

  function trackHotspotEvent(hotspot, type) {
    const hs = typeof hotspot === 'string' ? 
      tourFunctions.currentScene.getHotspot(hotspot) : hotspot;
      
    if (!hs) return;

    // Handle special case for tooltips style
    const hotspotType = hs.style === 'skin_tooltips' ? 
      `hotspot_${type}` : hs.style;

    const trackHotspot = {
      hotspot_id: hs.id,
      hotspot_title: hs.title ?? hs.tooltip,
      hotspot_type: hotspotType,
      scene_id: tourFunctions.currentScene.getId()
    };

    trackHotspotClick(trackHotspot);
  }

  const krpanoInfoClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:infoclick", e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openHotspotInfo(e.detail.hotspot);
    trackHotspotEvent(hs, 'info');
  };
  addEventListener("krpano:infoclick", krpanoInfoClickHandler);
  listeners.push({ type: "krpano:infoclick", listener: krpanoInfoClickHandler });

  const krpanoAccessibilityClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:accessibilityclick", e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openAccessibilityInfo(e.detail.hotspot);
    trackHotspotEvent(hs, 'accessibility');
  };
  addEventListener("krpano:accessibilityclick", krpanoAccessibilityClickHandler);
  listeners.push({ type: "krpano:accessibilityclick", listener: krpanoAccessibilityClickHandler });

  const krpanoGalleryClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:galleryclick", e);
    }
    const hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openGalleryInfo(hs);
    trackHotspotEvent(hs, 'gallery');
  };
  addEventListener("krpano:galleryclick", krpanoGalleryClickHandler);
  listeners.push({ type: "krpano:galleryclick", listener: krpanoGalleryClickHandler });

  function closeSocial() {
    popupSocial = false;
    sceneAudioPlaying = true;
    tourFunctions.focus();
  }

  const krpanoSocialClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:socialclick", e);
    }
    const scene = tour.scenes.find(s => s.id === e.detail.scene_id);
    const hotspot = scene?.hotspots.find(h => h.id === e.detail.hotspot_id);
    if (hotspot) {
      openSocialVideoFromHotspot(hotspot, "social");
    }
  };
  addEventListener("krpano:socialclick", krpanoSocialClickHandler);
  listeners.push({ type: "krpano:socialclick", listener: krpanoSocialClickHandler });

  const krpanoLinkClickHandler = (e) => {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("krpano:linkclick", e);
    }
    const { hotspot, link, hotspotId, sceneId } = e.detail;
    // Open link in new tab
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      // Track the hotspot event in the analytics
      const hs = tourFunctions.currentScene.getHotspot(hotspot);
      trackHotspotEvent(hs, 'link');
    }
  };
  addEventListener("krpano:linkclick", krpanoLinkClickHandler);
  listeners.push({ type: "krpano:linkclick", listener: krpanoLinkClickHandler });

  addEventListener("audio:gestureunlock", audioGestureUnlockHandler);
  listeners.push({ type: "audio:gestureunlock", listener: audioGestureUnlockHandler });

  let menuActive;
  let adaVisible = false;
  let instructionsVisible = false;
  let hasExecutedQuerystring = false;
  let hasAdaParam;
  let mapComponent;
  let mapUrlSyncHandler = null;
  let mapUrlSyncDebounceTimer = null;

  function teardownMapUrlSync() {
    if (mapComponent && mapUrlSyncHandler) {
      mapComponent.off("moveend", mapUrlSyncHandler);
    }
    mapUrlSyncHandler = null;
    if (mapUrlSyncDebounceTimer) {
      clearTimeout(mapUrlSyncDebounceTimer);
      mapUrlSyncDebounceTimer = null;
    }
  }

  function setupMapUrlSync() {
    if (!isMapboxMap || !expandedMap || !mapComponent) return;
    teardownMapUrlSync();

    mapUrlSyncHandler = () => {
      if (skipNextMapUrlSync) {
        skipNextMapUrlSync = false;
        return;
      }
      if (mapUrlSyncDebounceTimer) clearTimeout(mapUrlSyncDebounceTimer);
      mapUrlSyncDebounceTimer = setTimeout(() => {
        const view = getMapboxViewFromInstance(mapComponent);
        if (view) tourFunctions.setMapUrl(view);
      }, 300);
    };
    mapComponent.on("moveend", mapUrlSyncHandler);
  }

  $: if (isMapboxMap && expandedMap && mapComponent) {
    setupMapUrlSync();
  } else {
    teardownMapUrlSync();
  }
  // Handle Popup Ada & Instructions
  $: if (visibleUi && !hasExecutedQuerystring) {
    // ?ada query string
    const urlParams = new URLSearchParams(window.location.search);
    const adaSlug = tour.accessibilitySlug || "ada";
    const adaParam = urlParams.get(adaSlug);
    hasAdaParam = adaParam === "true" || adaParam === "";
    if (hasAdaParam) adaVisible = true;
    hasExecutedQuerystring = true; // Set the flag to true after execution
  }

  // Hash navigation for thumbs grid
  let thumbsGridComponent; // Reference to ThumbsGrid component

  /** When intro runs before ThumbsGrid exists, apply route once the grid is mounted. */
  let pendingIntroFeaturedApply = false;
  let pendingIntroFeaturedRoute = null;

  function queueOrApplyIntroFeaturedRoute(route) {
    const normalized = route === undefined ? null : route;
    if (thumbsGridComponent?.applyIntroFeaturedRoute) {
      thumbsGridComponent.applyIntroFeaturedRoute(normalized);
      return;
    }
    pendingIntroFeaturedRoute = normalized;
    pendingIntroFeaturedApply = true;
  }

  /** Intro “Start Tour” with routes: All = cancel pending / do nothing; route = open personalization + apply. */
  function handleIntroStartTourWithRoutes(selection) {
    if (!selection) {
      pendingIntroFeaturedApply = false;
      pendingIntroFeaturedRoute = null;
      return;
    }
    queueOrApplyIntroFeaturedRoute(selection);
  }

  $: if (visibleUi && thumbsGridComponent && pendingIntroFeaturedApply) {
    thumbsGridComponent.applyIntroFeaturedRoute(pendingIntroFeaturedRoute);
    pendingIntroFeaturedApply = false;
  }
  const isHashPersonalization = window.location.hash === "#/personalization";
  const isHashCategory = window.location.hash.match(/^#\/category\/(\d+)$/);
  const isHashCategoryAll = window.location.hash === "#/category/all";
  const isHashThumbGrid = isHashPersonalization || isHashCategory || isHashCategoryAll;
  
  $: if (isHashThumbGrid && visibleUi && thumbsGridComponent) {
    thumbsGridComponent.openThumbs();
    if (isHashPersonalization && (tour.features?.includes("personalization") || tour.features?.includes("personalizationButton"))) {
      thumbsGridComponent.activatePersonalization();
    } else if (isHashCategory && tour.features?.includes("categories")) {
      const categoryMatch = window.location.hash.match(/^#\/category\/(\d+)$/);
      const categoryIndex = categoryMatch ? parseInt(categoryMatch[1]) : null;
      if (isHashCategoryAll ||categoryIndex === null) {
        thumbsGridComponent.activateCategoryAll();
      } else {
        thumbsGridComponent.activateCategory(categoryIndex);
      }
    }
  }

  // Handle hotspot hash routing
  function handleHotspotHashLoaded({ hotspotId }) {
    if (!hotspotId) return;
    let hs = tourFunctions.currentScene.getHotspotById(hotspotId);
    // determine what type of hotspot it is
    if (hs.style === 'hotspot_gallery' || hs.style === 'hotspot_inline_photo_gallery') {
      openGalleryInfo(hs);
    } else if (hs.style === 'hotspot_social') {
      // do nothing
    } else if (hs.style === 'hotspot_accessibility') {
      hs = getHotspotContent(hs.name);
      openAccessibilityInfo(hs);
    } else if (hs.style === 'hotspot_info' || hs.style === 'hotspot_info_custom' || hs.style === 'hotspot_directions') {
      hs = getHotspotContent(hs.name);
      openHotspotInfo(hs);
    } else {
      hs = getHotspotContent(hs.name);
      openHotspotInfo(hs);
    }
  }



  function handleHamburgerAda() {
    adaVisible = true;
    menuActive = false;
  }

  function handleHamburgerInstructions() {
    instructionsVisible = true;
    menuActive = false;
  }

  function handleVr() {
    menuActive = false;
    // Create a new URL
    const url = new URL(window.location.href);
    url.search = ''; // remove existing query string
    url.hash = ''; // clear #hash links
    url.searchParams.set('vr', true);
    // Load the new URL
    window.location.href = url.toString();
  }

  function toggleInstructions() {
    instructionsVisible = !instructionsVisible;
  }

  function closeAda() {
    adaVisible = false;
    tourFunctions.focus();
  }

  function closeInstructions() {
    instructionsVisible = false;
    tourFunctions.focus();
  }

  function handlePersonalize() {
    if (thumbsGridComponent) {
      thumbsGridComponent.openThumbs();
      thumbsGridComponent.activatePersonalization();
    }
  }

  // Open Videos
  function closeVideo() {
    sceneAudioPlaying = true;
    tourFunctions.focus();
  }

  function handleVideoLoadEvent(e) {
    const { hotspot_id, scene_id, video: videoUrl, hotspot } = e.detail;

    const hotspotFromJson =
      hotspot_id && scene_id
        ? getHotspotFromTourJson(hotspot_id, scene_id)
        : null;

    if (hotspotFromJson?.popupSocialUrl) {
      openSocialVideoFromHotspot(hotspotFromJson, "video");
    } else {
      video = videoUrl;
      sceneAudioPlaying = false;
      const hs = tourFunctions.currentScene.getHotspot(hotspot);
      trackHotspotEvent(hs, "video");
    }

    if (process.env.NODE_ENV === "development") {
      console.log("krpano:videoclick", e);
    }
  }

  addEventListener("krpano:videoclick", handleVideoLoadEvent);
  listeners.push({ type: "krpano:videoclick", listener: handleVideoLoadEvent });

  // Scene logos
  // if(tour.sceneLogos){
  //     addEventListener('krpano:newpano', (e)=>{
  //         let sceneLogo = tourFunctions.currentScene.getLogo();
  //         tourLogo.url = sceneLogo ? sceneLogo : tour.logo.url;
  //     });
  // }

  function switchView() {
    expandedTour = !expandedTour;
    expandedMap = !expandedMap;
    expandedTour ? tourFunctions.showHotspots() : tourFunctions.hideHotspots();
    setMap();
    
    // Update URL hash
    if (expandedMap) {
      if (isMapboxMap) {
        const view = getMapboxViewFromInstance(mapComponent);
        tourFunctions.setMapUrl(view);
      } else {
        window.location.hash = "/map";
      }
    } else {
      tourFunctions.setStopUrl();
      queuePanoFocus();
    }

    // Track Map View / Scene View
    if (expandedMap) {
      trackSceneViewStart({
        scene_id: 'map',
        scene_title: 'map',
        scene_source: 'map_button',
      });
    } else {
      const scene = tourFunctions.currentScene.getScene();
      const scene_id = scene.id;
      const scene_title = scene.title;
      trackSceneViewStart({
        scene_id,
        scene_title,
        scene_source: 'map_button',
      });
    }
    // Clear the pending source after using it
    window.CC.pendingSceneSource = null;

    
    // Reset map when switching to small view (Mapbox) or refit image map (Leaflet CRS.Simple)
    if (!expandedMap && mapComponent) {
      if (process.env.NODE_ENV === 'development') {
        console.log("resetting map center");
      }
      const map = mapComponent;
      if (typeof map.fitImageMapToView === 'function') {
        requestAnimationFrame(() => map.fitImageMapToView());
        setTimeout(() => map.fitImageMapToView(), 50);
        setTimeout(() => map.fitImageMapToView(), 400);
      } else if (typeof map.jumpTo === 'function') {
        const mapOptions = isMobile && tour?.mapbox?.mobile ? {
          center: tour?.mapbox?.mobile?.mapCenter ?? tour?.mapbox?.mapCenter,
          zoom: tour?.mapbox?.mobile?.zoomMiniMap ?? tour?.mapbox?.zoomMiniMap,
          pitch: tour?.mapbox?.mobile?.mapPitch ?? tour?.mapbox?.mapPitch,
          bearing: tour?.mapbox?.mobile?.mapBearing ?? tour?.mapbox?.mapBearing
        } : {
          center: tour?.mapbox?.mapCenter,
          zoom: tour?.mapbox?.zoomMiniMap,
          pitch: tour?.mapbox?.mapPitch,
          bearing: tour?.mapbox?.mapBearing
        };
        map.jumpTo(mapOptions);
      }
    }

    

  }

  function setMap() {
    window.CC = window.CC || {};
    window.CC.switchView = switchView;
    setMapZoom();
  }
  if (hasMap) {
    setMap();
    if (initialMapView?.zoom != null) {
      zoom = initialMapView.zoom;
    }
  }

  window.CC = window.CC || {};
  window.CC.tourFunctions = tourFunctions;
  window.CC.currentSceneOrder = sceneState.currentOrder;

  // Track tour view if tracking feature is enabled
  async function trackTourView() {
    if (!hasTracking || !tour.id) return;
    
    try {
      const response = await fetch('https://api.vntpoint.com/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tourId: tour.id
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('Tour view tracked successfully:', result);
        }
      } else {
        console.error('Failed to track tour view:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error tracking tour view:', error);
    }
  }

  onDestroy(() => {
    teardownMapUrlSync();
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log("Tour destroyed");
    }
    tutorialOverlay.destroy();
    gallery?.$destroy();
    listeners.forEach(({ type, listener, target, capture }) => {
      (target || window).removeEventListener(type, listener, !!capture);
    });
  });
</script>

<!-- /*____________ Markup ____________*/ -->
{#if form && form.enabled && !hasAdaParam}
  <FormPopup
    {...form}
    tourReady={visibleUi}
    on:tutorialUnblock={() => tutorialOverlay.onFormDismissed()}
  />
{/if}
<div id="popup-container"></div>
<div id="popup-gallery"></div>
<Popup
  visible={adaVisible}
  on:close={closeAda}
  ariaLabel={$i18n.t('tour.accessibilityTour')}
  width={"100%"}
>
  <Ada {tour} />
</Popup>

<Popup visible={instructionsVisible} on:close={closeInstructions}>
  <Instructions />
</Popup>

{#if popupSocial && popupSocial.url && popupSocial.platform}
  <PopupSocial on:close={closeSocial} {...popupSocial} />
{/if}

{#if showIntroPopupVisible}
  <IntroPopup
    {tour}
    visible={showIntroPopupVisible}
    onApplyFeaturedRoute={handleIntroStartTourWithRoutes}
    on:close={() => {
      showIntroPopup = false;
      if (!visibleUi) {
        showUi();
      } else {
        queuePanoFocus();
      }
    }}
  />
{/if}

<!-- Map Loader - shows when accessing via #/map URL -->
{#if mapLoading && expandedMap}
  <Loader
    logo={logoLoading.url || undefined}
    loading={false}
    delay="3000"
    width={logoLoading.width}
    tour={tour}
    fontReadyTimeout={0}
    callback={() => {
      mapLoading = false;
      mapVisible = true;
      // Direct #/map entry can bypass little-planet completion; ensure UI chrome renders.
      visibleUi = true;
    }}
  />
{/if}

{#if visibleUi}
   <!-- {#if } -->
  <!-- Accessibility button (visible when on tour view; map menu is always visible on map) -->
  {#if !expandedMap}
    <button
      id="accessibility-tour-button"
      tabindex="0"
      on:click={() => { adaVisible = true; }}
    >
      Open Accessibility Tour
    </button>
  {/if}

  {#if tour.infoSidebar === true}
    <InfoSidebar on:loadvideo={handleVideoLoadEvent} logo={tourLogo?.url} />
  {/if}
  <!-- has map -->
  {#if hasMap && !isMobile}
    <MapButton click={switchView} {expandedMap} bgColor={theme?.primaryColor} />
  {/if}

  <Video bind:src={video} onClose={closeVideo} />

  <!-- no sidebar -->
  {#if !tour.infoSidebar}

    {#if !expandedMap}
      {#if tourLogo.url}
      <Logo
        linkto={hasSceneLogos ? null : (tour.logoUrl || null)}
        image={tourLogo.url}
        width={tourLogo.width}
        opacity="0.85"
      />
      {:else if logoText && (isMobile ? !tour.logoMobile?.disabled : !tour.logo?.disabled)}
      <TextLogo text={logoText || "Logo"} />
      {/if}
    {/if}

    {#if !hasSceneLogos && !expandedMap}
      {#if tour.features?.includes('multipleSceneButtons') && (tour.sceneButtons || []).length > 0}
        {#each (tour.sceneButtons || []) as buttonConfig, index}
          {@const targetScene = buttonConfig.sceneId ? visibleScenes.find(s => s.id === buttonConfig.sceneId) : visibleScenes?.[0]}
          {@const buttonLabel = buttonConfig.label ?? 'Overview'}
          {@const calculateAutoWidth = (label) => {
            // Estimate width needed for text to fit on one line
            // Font size is 1.4rem (22.4px), each character is roughly 0.6em wide
            // Plus padding (16px total) and some buffer
            const charWidth = isMobile ? 10 : 13; // Approximate pixel width per character
            const padding = 16;
            const minWidth = 120;
            const calculatedWidth = (label.length * charWidth) + padding;
            return Math.max(calculatedWidth, minWidth);
          }}
          {@const autoWidth = buttonConfig.autoSize !== false ? calculateAutoWidth(buttonLabel) : tourLogo.width * 0.8}
          {@const buttonWidth = getDimension(
            buttonConfig.autoSize !== false,
            autoWidth,
            buttonConfig.width,
            buttonConfig.mobileWidth,
          )}
          {@const defaultButtonX = tourLogo.width / 2 - buttonWidth / 2 + 20}
          {@const buttonX = getDimension(
            buttonConfig.autoSize !== false,
            defaultButtonX,
            buttonConfig.x,
            buttonConfig.mobileX,
          )}
          {@const baseY = tourLogo.height + 25}
          {@const buttonSpacing = 80}
          {@const defaultButtonY = buttonConfig.autoSize !== false ? baseY + (index * buttonSpacing) : baseY}
          {@const buttonY = getDimension(
            buttonConfig.autoSize !== false,
            defaultButtonY,
            buttonConfig.y,
            buttonConfig.mobileY,
          )}
          {@const buttonBgColor = buttonConfig.bgColor || sceneButton?.bgColor || theme?.primaryColor || "#000000"}
          {@const buttonTextColor = buttonConfig.textColor || sceneButton?.textColor || "#FFFFFF"}
          {#if buttonConfig.enabled !== false && targetScene}
            <SceneButton
              text={buttonLabel}
              x={buttonX}
              y={buttonY}
              width={buttonWidth}
              opacity={0.8}
              scene={targetScene}
              bgColor={buttonBgColor}
              textColor={buttonTextColor}
              showArrow={false}
              alwaysShow={true}
            />
          {/if}
        {/each}
      {:else if sceneButtonSettings?.enabled !== false}
        <SceneButton
          text={sceneButtonLabel}
          x={sceneButtonX}
          y={sceneButtonY}
          width={sceneButtonWidth}
          opacity={0.8}
          scene={visibleScenes?.[0]}
        />
      {/if}
    {/if}
  {/if}

  <div class="top-left" style={`right: ${embedAdjust}px; left: auto;`}>
    {#if callToAction}
      <CallToAction {callToAction} />
    {/if}

    {#if menu}
      <HamburgerMenu
        menus={menu}
        {tour}
        bind:active={menuActive}
        on:ada={handleHamburgerAda}
        on:instructions={handleHamburgerInstructions}
        on:vr={handleVr}
      />
    {/if}
  </div>

  {#if !tour.features?.includes("whitelabel")}
    <WatermarkLogo />
  {/if}

  {#if expandedTour}
    {#if !isMobile && tour.scenes.length > 1}
      <PrevScene {tour} {sceneState} />
      <NextScene {tour} {sceneState} />
    {/if}
    {#if !(isMobile && mobileDescriptionOpen)}
      <SceneTitle isStatic={hasStaticSceneTitles} />
    {/if}
    <ThumbsGrid
      title={tourTitle}
      showPrevNext={false}
      bind:this={thumbsGridComponent}
      {tour}
      {sceneState}
      {updateSceneOrder}
    />
  {/if}

  <!-- Desktop Scene Description -->
  {#if hasAnySceneAudio || tour.features?.includes('sceneDescriptions')}
    <SceneDescription
      {tour}
      hasMap={hasMap}
      visible={expandedTour}
      playing={sceneAudioPlaying !== false && expandedTour}
      loop={audioSettings?.looping || false}
      resumable={audioSettings?.resumable || true}
      enableSceneAudio={hasAnySceneAudio && tour.features?.includes('audio')}
      enableDescriptionAudio={tour.features?.includes('sceneDescriptions')}
      hasPersonalizationButton={tour.features?.includes('personalizationButton')}
      onPersonalize={handlePersonalize}
      onPlayingChange={(isPlaying) => sceneAudioPlaying = isPlaying}
    />
  {/if}

  <!-- Standalone PersonalizationButton for desktop when SceneDescription doesn't render (hidden on map view) -->
  {#if !isMobile && tour.features?.includes('personalizationButton') && !hasAnySceneAudio && !tour.features?.includes('sceneDescriptions') && !expandedMap}
    <PersonalizationButton hasMap={hasMap} on:personalize={handlePersonalize} />
  {/if}

  <!-- Mobile Controls - handles all mobile button positioning and description -->
  <MobileControls
    hasPersonalization={tour.features?.includes('personalizationButton')}
    hasDescription={tour.features?.includes('sceneDescriptions')}
    hasAudio={hasAnySceneAudio && (tour.features?.includes('audio') || tour.features?.includes('sceneDescriptions'))}
    hasMap={hasMap}
    {expandedMap}
    showLocationMenu={showLocationMenu}
    {tour}
    playing={sceneAudioPlaying !== false && expandedTour}
    loop={audioSettings?.looping || false}
    resumable={audioSettings?.resumable || true}
    enableSceneAudio={hasAnySceneAudio && tour.features?.includes('audio')}
    enableDescriptionAudio={tour.features?.includes('sceneDescriptions')}
    onPlayingChange={(isPlaying) => sceneAudioPlaying = isPlaying}
    {theme}
    onOpenLocations={(opts) => {
      if (!showLocationMenu) return;
      if (opts?.focusFilter) {
        mobileLocationsOpen = true;
        focusLocationsFilterOnOpen = true;
      } else {
        mobileLocationsOpen = !mobileLocationsOpen;
        focusLocationsFilterOnOpen = false;
      }
    }}
    locationsSheetOpen={mobileLocationsOpen}
    on:personalize={handlePersonalize}
    on:mapClick={switchView}
    on:descriptionOpen={(e) => mobileDescriptionOpen = e.detail.open}
  />
{/if}

{#if !hasMap}
  <div class="krpano-view-wrapper">
    <Krpano
      config={tour}
      {colors}
      expanded={expandedTour}
      xml="tour.xml"
      logoLoading={logoLoading.url || undefined}
      logoLoadingWidth={logoLoading.width}
      onHotspotHashLoaded={handleHotspotHashLoaded}
      loaderEnabled={!showMapFirst}
    />
    <TutorialOverlay
      visible={showTutorialOverlay === true && expandedTour}
      isMobileOrTablet={isMobileOrTablet}
      onDismiss={dismissTutorialOverlay}
    />
  </div>
{/if}

<!-- Has Map -->
{#if hasMap}
  <div
    class:has-audio={hasAnySceneAudio && !expandedMap}
    class={expandedTour ? "large-view" : "small-view"}
    class:loading={mapLoading}
    transition:fly={!expandedTour ? FLY_FROM_BOTTOM : NO_FLY}
  >
    <button
      class="hide-large view-switch-button"
      on:click={switchView}
      aria-label={$i18n.t('tour.showTour')}
    >
      <div class="switch-hover">{$i18n.t('tour.showTour')}</div>
      <SwitchViews />
    </button>
    <div class="krpano-view-wrapper" inert={!expandedTour}>
      <Krpano
        config={tour}
        {colors}
        expanded={expandedTour}
        xml="tour.xml"
        logoLoading={logoLoading.url || undefined}
        logoLoadingWidth={logoLoading.width}
        onHotspotHashLoaded={handleHotspotHashLoaded}
        loaderEnabled={!showMapFirst}
      />
      <TutorialOverlay
        visible={showTutorialOverlay === true && expandedTour}
        isMobileOrTablet={isMobileOrTablet}
        onDismiss={dismissTutorialOverlay}
      />
    </div>
  </div>

  {#if mapVisible}
    <div
      class:has-audio={hasAnySceneAudio && expandedTour}
      class={expandedMap ? "large-view" : "small-view"}
      class:map-with-personalization-offset={!expandedMap && !isMobile && hasMap && tour.features?.includes('personalizationButton') && !hasAnySceneAudio && !tour.features?.includes('sceneDescriptions')}
      class:map-dock-right-desktop={!expandedMap && !isMobile && tour.features?.includes('sceneDescriptions')}
      class:map-dock-right-clear-watermark={!expandedMap && !isMobile && tour.features?.includes('sceneDescriptions') && !tour.features?.includes('whitelabel')}
      class:locations-sheet-open={isMobile && mobileLocationsOpen}
      transition:fly={!expandedMap ? FLY_FROM_BOTTOM : NO_FLY}
    >
      <button
        class="hide-large view-switch-button"
        on:click={switchView}
        aria-label={$i18n.t('tour.showMap')}
      >
        <div class="switch-hover">{$i18n.t('tour.showMap')}</div>
        <SwitchViews />
      </button>
      <div inert={!expandedMap}>
        {#if tour.mapbox?.mapDisplayMode === "image"}
          <ImageMap
            {tour}
            expanded={expandedMap}
            {zoom}
            bind:mapComponent
            showLocationsMenu={showLocationMenu}
            mobileLocationsOpen={showLocationMenu ? mobileLocationsOpen : false}
            onCloseMobileLocations={() => { mobileLocationsOpen = false; focusLocationsFilterOnOpen = false; }}
            focusLocationsFilterOnOpen={showLocationMenu ? focusLocationsFilterOnOpen : false}
            onFocusLocationsFilterDone={() => focusLocationsFilterOnOpen = false}
          />
        {:else}
          <Mapbox
            {tour}
            expanded={expandedMap}
            {zoom}
            {initialMapView}
            bind:mapComponent
            showLocationsMenu={showLocationMenu}
            mobileLocationsOpen={showLocationMenu ? mobileLocationsOpen : false}
            onCloseMobileLocations={() => { mobileLocationsOpen = false; focusLocationsFilterOnOpen = false; }}
            focusLocationsFilterOnOpen={showLocationMenu ? focusLocationsFilterOnOpen : false}
            onFocusLocationsFilterDone={() => focusLocationsFilterOnOpen = false}
          />
        {/if}
      </div>
      {#if expandedMap && visibleUi && !tour.infoSidebar}
        {#if mapLogo?.url}
          <Logo
            linkto={hasSceneLogos ? null : (tour.logoUrl || null)}
            image={mapLogo.url}
            width={mapLogo.width}
            opacity="0.85"
          />
        {:else if logoText && (isMobile ? !tour.logoMobile?.disabled : !tour.logo?.disabled)}
          <TextLogo text={logoText || "Logo"} />
        {/if}
      {/if}
    </div>
  {/if}
{/if}
<!-- END Has Map -->

<div
  aria-live="assertive"
  id="liveFeedback"
  bind:this={liveFeedback}
  style="position: absolute; left: -9999px; top: -9999px;"
></div>

<!-- /*____________ CSS ____________*/ -->
<style>
  .krpano-view-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  #accessibility-tour-button {
  position: absolute;
  top: -9999px;
  left: 0;
  width: 100%;
  padding: 0.5rem;
  border: none;
  background: #f7c60f;
  color: #001eff;
  font-size: 1rem;
  cursor: pointer;
  z-index: 9999;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  opacity: 0;
  font-weight: 700;
}

#accessibility-tour-button:focus {
  opacity: 1;
  top: 0;
}


  :global(.clicktext) {
    font-weight: 900;
    font-size: 14px;
    text-decoration: underline;
  }

  .top-left {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 999;
    width: 100%;
  }

  .close-embed {
    position: fixed;
    top: 0;
    right: 0;
    padding: 10px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    cursor: pointer;
    z-index: 999;
  }

  .view-switch-button {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    z-index: 10;
  }

  .view-switch-button:focus-visible {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
  }

  .switch-hover {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    position: absolute;
    left: 0;
    top: 0;
    z-index: 9;
    color: #fff;
    display: none;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;

    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .small-view:hover .switch-hover,
  .small-view .view-switch-button:focus .switch-hover {
    display: flex;
  }

  .small-view {
    display: block;
    width: 300px;
    height: 200px;

    position: absolute;
    bottom: 0;
    left: 0;
    right: auto;
    z-index: 800;

    margin: 20px;

    border: 3px solid white;
    border-radius: 10px;
    box-shadow: -3px 3px 10px #000;
    background: white;
  }

  .small-view.loading {
    opacity: 0;
    visibility: hidden;
  }

  .small-view.has-audio {
    bottom: 40px;
  }

  /* Desktop: whichever view is the small preview (tour or map) sits above the personalization button */
  .small-view.map-with-personalization-offset {
    margin-bottom: 60px;
  }

  /* Desktop: scene descriptions live bottom-left; dock the map preview on the right */
  .small-view.map-dock-right-desktop {
    left: auto;
    right: 0;
    bottom: 0;
  }

  /* Extra inset only when the Cove watermark is shown (not whitelabeled) */
  .small-view.map-dock-right-desktop.map-dock-right-clear-watermark {
    margin-bottom: 56px;
    margin-right: 8px;
  }

  .large-view {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  /* When mobile locations sheet is open, raise map view above logo/hamburger (z-index 999) */
  .large-view.locations-sheet-open {
    z-index: 1000;
  }

  .large-view .hide-large {
    display: none;
  }

  :global(.large-view .mapboxgl-map) {
    height: 100vh;
    width: 100vw;
  }

  /* Leaflet image map: same sizing as Mapbox — parent .map had no height so 100% on .leaflet-container collapsed to 0 */
  :global(.large-view .map.map-image) {
    height: 100vh;
    width: 100vw;
  }

  :global(.small-view .mapboxgl-map) {
    height: 200px;
    width: 300px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }

  :global(.small-view .map.map-image) {
    height: 200px;
    width: 300px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }

  :global(*:focus-visible) {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
    border-radius: 0 !important;
  }

  @media only screen and (max-width: 600px) {
    .small-view {
      display: none;
    }
  }


</style>
